<script setup lang="ts">
import { modifyDate } from '@/shared/utils/modifyTime';
import { ROOT_JOURNAL_URL } from '@/shared/constants/urlConstants';
import type { IJournalArticle } from '@/entities/journal/model/types';

const props = withDefaults(
  defineProps<{
    card: IJournalArticle;
    isDetailPage?: boolean;
  }>(),
  {
    isDetailPage: false,
  },
);

const isMobile: boolean = useIsMobile();
const isShowFullInfo: Ref<boolean> = ref(false);

const cardLink: ComputedRef<string> = computed(() =>
  props.card.topic?.slug
    ? `${ROOT_JOURNAL_URL}${props.card.topic.slug}/${props.card.slug}/`
    : ROOT_JOURNAL_URL,
);

const viewsCount: ComputedRef<number> = computed(
  () => props.card.view_count ?? props.card.views ?? 0,
);

const imageSrc: ComputedRef<string> = computed(
  () => props.card.content?.preview_thumbnail || props.card.content?.preview || '',
);
const imageSrcset: ComputedRef<string | undefined> = computed(() => {
  const thumb = props.card.content?.preview_thumbnail;
  const thumb2x = props.card.content?.preview_thumbnail_2x;
  return thumb && thumb2x ? `${thumb} 1x, ${thumb2x} 2x` : undefined;
});

const priorityCardIds = inject<ComputedRef<number[]> | null>('priorityCardIds', null);

const isPriorityCard: ComputedRef<boolean> = computed(
  () => priorityCardIds?.value.includes(props.card.id) ?? false,
);

const imageLoading: ComputedRef<'eager' | 'lazy'> = computed(() =>
  isPriorityCard.value ? 'eager' : 'lazy',
);

const imageFetchPriority: ComputedRef<'high' | undefined> = computed(() =>
  isPriorityCard.value ? 'high' : undefined,
);

const showFullInfo = (): void => {
  isShowFullInfo.value = true;
};

const hideFullInfo = (): void => {
  isShowFullInfo.value = false;
};
</script>

<template>
  <NuxtLink
    :class="[
      'journal-card',
      { _mobile: isMobile },
      { _show: isShowFullInfo },
      { _detail: isDetailPage },
    ]"
    :to="cardLink"
  >
    <div class="journal-card__inner" @mouseover="showFullInfo" @mouseleave="hideFullInfo">
      <img
        class="journal-card__image"
        :src="imageSrc"
        :srcset="imageSrcset"
        :alt="card.content?.title ?? 'Превью статьи'"
        width="285"
        height="180"
        :loading="imageLoading"
        :fetchpriority="imageFetchPriority"
      />
      <div class="journal-card__content">
        <div class="journal-card__header">
          <div class="journal-card__title">
            {{ card.content?.title }}
          </div>
          <div class="journal-card__desc">
            {{ card.content?.description }}
          </div>
        </div>
        <div class="journal-card__info">
          <div class="journal-card__date">
            <AppIcon class="journal-card__icon" name="EyeSmall" />
            {{ viewsCount }} <span class="journal-card__views">просмотров</span>
            <time>{{ modifyDate(card.published_at) }}</time>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style lang="less">
.journal-card {
  display: block;
  overflow: hidden;
  position: relative;
  scroll-snap-align: center;
  height: 250px;
  width: 100%;
  border-radius: @size-x3;

  &._show &__inner {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0.5;
      background-color: #000;
      animation: journal-card-opacity @A ease-in-out;
    }
  }

  &__image {
    display: block;
    object-fit: cover;
    max-width: 100%;
    width: 100%;
    height: 180px;
    transition: transform @A ease-in-out;
  }

  &._show &__image {
    transform: scale(1.2);
  }

  &__content {
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 0;
    z-index: 2;
    width: 100%;
    height: 95px;
    background-color: white;
    border: 1px solid @color-gallery;
    border-radius: @size-x3;
    padding: @size-x3 @size-x5;
    transition: height @A ease-in-out;
  }

  &._show &__content {
    height: 159px;
  }

  &__header {
    .flex-column(@main-size);
  }

  &__title {
    .overflow-ellipsis-lines();

    font-size: @size-x4;
    line-height: 21px;
    font-weight: 700;
  }

  &__desc {
    .overflow-ellipsis-lines(3);

    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    color: @color-silver-chalice;
    opacity: 0;
    transition: opacity @A ease-in-out;
  }

  &._show &__desc {
    opacity: 1;
  }

  &__info {
    position: absolute;
    bottom: @size-x3;
  }

  &__date {
    display: inline-flex;
    align-items: center;
    gap: @main-size;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
  }

  &__icon {
    width: @size-x3;
    height: @size-x3;
  }

  &__views {
    color: @color-silver-chalice;
  }

  &._mobile:not(._detail) {
    min-width: 280px;
    max-width: 280px;
  }

  &._mobile &__content {
    padding: @size-x4 @size-x4 35px;
  }
}

@keyframes journal-card-opacity {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 0.5;
  }
}
</style>
